import { MEASUREMENT_LABELS, measurementDisplay, type Measurement } from '~/data/weighins'
import { formatMetric } from '~/data/metrics'
import { NUTRIENT_LABELS, formatCalories, formatGrams } from '~/data/nutrition'
import type { Units } from '~/data/types'
import { formatWeight } from '~/lib/units'

/**
 * What a reading the app records says, in words. It lives apart from anything that draws one —
 * the metrics row's cards, the weigh-in's own fields, a day's heading — so a weight, a girth or
 * a count of steps is written the same way wherever it is read.
 */

/** The diary's readings: the days' intake, and the expenditure. */
export type NutritionKind = 'calories' | 'protein' | 'carbs' | 'fat' | 'expenditure'

export const NUTRITION_KINDS: readonly NutritionKind[] = [
  'calories',
  'protein',
  'carbs',
  'fat',
  'expenditure',
]

/**
 * What a reading can be about: the two weight series, the day's steps, a measurement, or
 * one of the diary's.
 */
export type ReadingKind = 'scale' | 'trend' | 'steps' | Measurement | NutritionKind

/** Whether a reading is a weight, which is the only thing the account's units apply to. */
export const isWeight = (kind: ReadingKind) => kind === 'scale' || kind === 'trend'

export const isNutrition = (kind: ReadingKind): kind is NutritionKind =>
  (NUTRITION_KINDS as readonly string[]).includes(kind)

export function readingLabel(kind: ReadingKind): string {
  if (kind === 'scale') return 'Scale weight'
  if (kind === 'trend') return 'Trend weight'
  if (kind === 'steps') return 'Steps'
  if (kind === 'expenditure') return 'Expenditure'
  if (kind === 'calories' || kind === 'protein' || kind === 'carbs' || kind === 'fat') {
    return NUTRIENT_LABELS[kind]
  }
  return MEASUREMENT_LABELS[kind]
}

/** A reading as it is said, in the unit the reading itself takes. */
export function readingOf(kind: ReadingKind, value: number, units: Units): string {
  if (isWeight(kind)) {
    return formatWeight(value, units)
  }
  // Steps are a count and wear no unit: "9,412 steps" is what a chip says, and the chip's own
  // label is already the word — a card naming both would say it twice.
  if (kind === 'steps') {
    return formatMetric('steps', value)
  }
  if (kind === 'calories' || kind === 'expenditure') {
    return formatCalories(value)
  }
  if (kind === 'protein' || kind === 'carbs' || kind === 'fat') {
    return formatGrams(value)
  }
  const read = measurementDisplay(value)
  // A girth is centimetres and body fat is a percentage — see the weigh-in's fields.
  return kind === 'bodyFat' ? `${String(read)}%` : `${String(read)} cm`
}
